import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { Avatar } from "@whoosh/design-system/src/components/display";
import { MarkdownDisplay } from "@whoosh/design-system/src/components/display/MarkdownDisplay/MarkdownDisplay";
import {
  HStack,
  VStack,
  Text,
} from "@whoosh/design-system/src/components/primitive";

import { useFacilityLessonNoun } from "../../../../../features/Facility";
import { useFacilityShowLessonPrices } from "../../../../../features/Facility/hooks/useFacilityShowLessonPrices";
import { PriceDisplay } from "../../../components/PriceDisplay";
import { useGetLessonTypeOptions } from "../../../hooks/useGetLessonTypeOptions";
import { InstructorBioQuery } from "./__generated__/InstructorBioQuery.graphql";

export const InstructorBio = ({
  queryRef,
}: {
  queryRef: PreloadedQuery<InstructorBioQuery>;
}) => {
  const data = usePreloadedQuery(InstructorBioQueryString, queryRef);
  const lessonTypes = useGetLessonTypeOptions(
    data?.node ?? null,
    data?.facility ?? null,
    data?.session ?? null
  );
  const showLessonPrices = useFacilityShowLessonPrices(data?.facility ?? null);
  const { plural: lessonsNoun } = useFacilityLessonNoun(data?.facility ?? null);

  return (
    <VStack px={6} gap={4} pt={4}>
      <HStack justifyContent="space-between" alignItems="center" gap={4}>
        <VStack gap={3} flex={1} minWidth={0}>
          <Text variant="mainTitle2" color="text-primary">
            {data?.node?.user?.fullName}
          </Text>
          <Text
            variant="mainSmallMedium"
            color="text-secondary"
            numberOfLines={2}
            ellipsizeMode="tail"
          >
            {data?.node?.instructorDepartmentProfile?.headline}
          </Text>
        </VStack>
        <Avatar
          profileImagePublicId={data?.node?.profileImage?.publicId}
          firstName={data?.node?.user?.firstName}
          lastName={data?.node?.user?.lastName}
          type="staff"
          size="lg"
        />
      </HStack>
      <VStack gap={8}>
        <VStack gap={4}>
          <Text variant="mainSmallSemiBold" color="text-secondary">
            {`${t`Available`} ${lessonsNoun}`}
          </Text>
          <VStack gap={4}>
            {lessonTypes?.map((lessonType) =>
              lessonType?.options?.map((option) => (
                <HStack justifyContent="space-between" key={option.value}>
                  <Text>{t`${lessonType.label} (${
                    option.duration ?? 0
                  }m)`}</Text>
                  {showLessonPrices && option.priceRange ? (
                    <PriceDisplay priceRange={option.priceRange} />
                  ) : null}
                </HStack>
              ))
            )}
          </VStack>
        </VStack>
      </VStack>
      {data.node?.instructorDepartmentProfile?.bio ? (
        <VStack gap={4}>
          <Text variant="mainSmallSemiBold" color="text-secondary">
            {t`Bio`}
          </Text>
          <MarkdownDisplay
            markdown={data.node.instructorDepartmentProfile.bio}
          />
        </VStack>
      ) : null}
    </VStack>
  );
};

export const InstructorBioQueryString = graphql`
  query InstructorBioQuery(
    $facilitySlug: String!
    $instructorId: ID!
    $membershipTypeId: ID!
  ) {
    node(id: $instructorId) {
      ... on StaffProfile {
        instructorDepartmentProfile(facilitySlug: $facilitySlug) {
          headline
          bio
        }
        profileImage {
          # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
          publicId: renderingKey
        }
        user {
          fullName
          firstName
          lastName
        }
      }
      ...useGetLessonTypeOptions_staffProfile
    }
    facility(slug: $facilitySlug) {
      ...useGetLessonTypeOptions_facility
      ...useFacilityShowLessonPrices_facility
      ...useFacilityLessonNoun_facility
    }
    session {
      ...useGetLessonTypeOptions_session
    }
  }
`;
